<script lang="ts" setup>
import { ref, watch, onMounted } from 'vue'
import { storeToRefs } from 'pinia'
import { QInfiniteScroll } from 'quasar'
import { useBountiesStore } from '../stores/bounties'
import { useSessionStore } from '../stores/session'
import PreviewBountyCard from '../components/bounty/PreviewBountyCard.vue'

const sessionStore = useSessionStore()
const { isLoggedIn } = storeToRefs(sessionStore)

const bountiesStore = useBountiesStore()
const { bounties, sort, isLoading, hasMore } = storeToRefs(bountiesStore)

const infiniteScrollRef = ref<InstanceType<typeof QInfiniteScroll> | null>(null)
const firstLoadComplete = ref(false)
const sortOptions = [
  { value: '-createdAt', label: 'Latest', icon: 'mdi-clock-outline' },
  { value: '-rewardAmountPoints', label: 'Highest Reward', icon: 'mdi-star-circle' }
] as const

onMounted(async () => {
  bountiesStore.reset()
  await bountiesStore.fetchBounties()
  firstLoadComplete.value = true
})

async function onLoad(_index: number, done: (stop?: boolean) => void) {
  if (!hasMore.value) {
    done(true)
    return
  }
  await bountiesStore.fetchBounties()
  done(!hasMore.value)
}

function loadMore() {
  infiniteScrollRef.value?.trigger()
}

watch(sort, async () => {
  firstLoadComplete.value = false
  bountiesStore.reset()
  await bountiesStore.fetchBounties()
  firstLoadComplete.value = true
})
</script>

<template>
  <q-page padding>
    <div class="row q-mb-md items-start q-col-gutter-y-sm">
      <div class="col-12 col-sm">
        <h4 class="text-h4 text-weight-bold q-my-none text-blue-grey-9">Bounties</h4>
        <p class="text-body2 text-blue-grey-7 q-mb-none">
          People are looking for footage from these incidents. Upload your footage to earn points.
        </p>
      </div>
      <div class="col-12 col-sm-auto">
        <q-btn-toggle
          v-model="sort"
          flat
          toggle-color="blue-8"
          :options="sortOptions.map((o) => ({ value: o.value, label: o.label }))"
        />
      </div>
    </div>

    <q-inner-loading :showing="!firstLoadComplete" />

    <template v-if="firstLoadComplete">
      <div v-if="bounties.length === 0" class="text-center text-blue-grey-5 q-pa-xl">
        <q-icon name="mdi-bullhorn-outline" size="4rem" class="q-mb-md" />
        <div class="text-h6">No bounties posted yet</div>
        <div class="text-body2 q-mt-sm">Be the first to post one.</div>
        <q-btn
          class="q-mt-md"
          color="primary"
          label="Submit a Bounty"
          :to="{ name: 'submit-bounty' }"
          v-if="isLoggedIn"
        />
      </div>

      <q-infinite-scroll v-else ref="infiniteScrollRef" @load="onLoad" :offset="250">
        <div class="row q-col-gutter-md">
          <div
            v-for="bounty in bounties"
            :key="bounty.id"
            class="col-12 col-md-6 col-lg-4 col-xl-2"
          >
            <preview-bounty-card :bounty="bounty" />
          </div>
        </div>
        <template #loading>
          <div class="row justify-center q-my-md">
            <q-spinner-dots color="primary" size="40px" />
          </div>
        </template>
      </q-infinite-scroll>

      <div v-if="hasMore && !isLoading" class="row justify-center q-my-md">
        <q-btn flat color="primary" label="Load more" icon="mdi-chevron-down" @click="loadMore" />
      </div>
    </template>
  </q-page>
</template>
